import React, { useState, useEffect } from 'react'
import { Card, Spin, notification, Empty, Tree } from 'antd'
import { FolderOpenOutlined, FileOutlined, FileTextOutlined, FilePdfOutlined } from '@ant-design/icons'
import { useLanguage } from '../../../components/context/LanguageContext'
import { apiRequest } from '../../../helpers'

const LicitatiiCentralizatorTab = ({ agent }) => {
  const languageContext = useLanguage()
  const [loading, setLoading] = useState(false)
  const [centralizatorData, setCentralizatorData] = useState([])

  useEffect(() => {
    if (agent?.id) {
      fetchCentralizatorData()
    }
  }, [agent?.id])

  const fetchCentralizatorData = async () => {
    setLoading(true)
    try {
      const response = await apiRequest(`${process.env.REACT_APP_API}agents/licitatii/${agent.id}/centralizator-folders`, 'GET')

      if (response.ok) {
        setCentralizatorData(response.data || [])
      } else {
        notification.error({
          message: languageContext.t('Error'),
          description: languageContext.t('Failed to load centralizator data'),
        })
      }
    } catch (error) {
      console.error('Error loading centralizator data:', error)
      notification.error({
        message: languageContext.t('Error'),
        description: languageContext.t('Failed to load centralizator data'),
      })
    }
    setLoading(false)
  }

  const getFileIcon = fileName => {
    if (!fileName) return <FileOutlined />
    const extension = fileName.toLowerCase().split('.').pop()

    switch (extension) {
      case 'pdf':
        return <FilePdfOutlined style={{ color: '#ff4d4f' }} />
      case 'doc':
      case 'docx':
        return <FileTextOutlined style={{ color: '#1890ff' }} />
      default:
        return <FileOutlined />
    }
  }

  const prepareTreeData = () => {
    return centralizatorData.map(licitatie => ({
      key: `licitatie-${licitatie.id}`,
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FolderOpenOutlined style={{ color: '#faad14' }} />
          <span style={{ fontWeight: 'bold' }}>{licitatie.name}</span>
          <span
            style={{
              backgroundColor: '#e6f7ff',
              color: '#1890ff',
              padding: '2px 6px',
              borderRadius: '10px',
              fontSize: '11px',
            }}
          >
            {licitatie.files?.length || 0} files
          </span>
        </div>
      ),
      children:
        licitatie.files?.map((file, index) => ({
          key: `file-${licitatie.id}-${index}`,
          title: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {getFileIcon(file.noticeDocumentName)}
              <span>{file.noticeDocumentName}</span>
              <span style={{ color: '#666', fontSize: '12px' }}>({file.localFilePath ? 'Local' : 'Remote'})</span>
            </div>
          ),
          isLeaf: true,
        })) || [],
    }))
  }

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>{languageContext.t('Loading centralizator data...')}</div>
        </div>
      </Card>
    )
  }

  if (!centralizatorData || centralizatorData.length === 0) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div>
              <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>{languageContext.t('No processable licitatii found')}</div>
              <div style={{ color: '#666', fontSize: '14px' }}>
                Only licitatii with local files (non-p7s and non-p7m) are shown here for processing.
              </div>
            </div>
          }
        />
      </Card>
    )
  }

  const treeData = prepareTreeData()

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FolderOpenOutlined style={{ color: '#faad14' }} />
          {languageContext.t('Centralizator Licitatii')}
          <span
            style={{
              backgroundColor: '#f6ffed',
              color: '#52c41a',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 'normal',
            }}
          >
            {centralizatorData.length} licitatii
          </span>
        </div>
      }
      extra={<span style={{ color: '#666', fontSize: '12px' }}>{languageContext.t('Processable files ready for AI analysis')}</span>}
    >
      <div style={{ maxHeight: '600px', overflow: 'auto' }}>
        <Tree
          treeData={treeData}
          defaultExpandAll
          showLine={{ showLeafIcon: false }}
          blockNode
          style={{
            backgroundColor: 'transparent',
          }}
        />
      </div>

      <div
        style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#f0f8f0',
          borderRadius: '6px',
          border: '1px solid #d9f7be',
        }}
      >
        <div style={{ fontSize: '12px', color: '#389e0d' }}>
          <strong>ℹ️ Info:</strong> This shows licitatii from the centralizator that have:
          <ul style={{ margin: '4px 0 0 16px' }}>
            <li>Files with localFilePath set (downloaded locally)</li>
            <li>Documents without .p7s or .p7m extensions</li>
          </ul>
          These licitatii are ready to be processed by the AI agent.
        </div>
      </div>
    </Card>
  )
}

export default LicitatiiCentralizatorTab
